import React, { useState } from "react";
import { Section } from "../../Section/Section";

import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { HeaderAudiosType, HeaderVideosType } from "@/types/PageType";
import { Post } from "@aleksundshantu/schirdewan-standard-types/client";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { customSanitize } from "@/utils/customSanitize";
import styles from "./AVHeader.module.scss";
import { ContentBlockerWrapper } from "@aleksundshantu/aunds-cookie-plugin-react";
import VidstackAudioPlayer from "@/components/VidstackPlayer/VidstackAudioPlayer";
import VidstackPlayer from "@/components/VidstackPlayer/VidstackPlayer";

interface AVHeaderProps {
  type: "videos" | "audios";
  post: Post<"videos"> | Post<"audios">;
}

export const AVHeader = ({ type, post }: AVHeaderProps) => {
  const { getLabel } = useGlobalContext();
  const isVideo = type === "videos";
  const videoData = isVideo
    ? ((post as Post<"videos">).acf.header_videos as HeaderVideosType)
    : null;
  const audioData = !isVideo
    ? ((post as Post<"audios">).acf.header_audios as HeaderAudiosType)
    : null;
  const transcript = isVideo ? videoData?.transcript : audioData?.transcript;
  const audioDescriptionVideoDesktop = isVideo
    ? videoData?.audio_description_video_desktop
    : null;

  const [activeVideo, setActiveVideo] = useState<
    "original" | "audio-description"
  >("original");

  function handleTabChange(newTab: string) {
    setActiveVideo(newTab as "original" | "audio-description");
  }
  const originalVideo =
    isVideo && videoData ? (
      <VidstackPlayer
        autoplay={false}
        loop={false}
        muted={false}
        video={videoData}
        video_decorative={false}
        singlePage={true}
        sizes="100vw, (min-width:768px) 80vw, (min-width: 1024px) 67vw"
      />
    ) : null;

  return (
    <header className={`${styles.header} has-body-bg`}>
      <h1 className="h1-pages has-text-centered pt-6">{post.title.rendered}</h1>
      <Section columnsClassName="is-centered">
        <div className="column is-10-tablet is-8-desktop is-8-widescreen">
          {isVideo && videoData && (
            <ul className={styles["video-tabs"] + " is-flex"}>
              <li
                className={`${styles.tab} ${
                  activeVideo === "original" ? styles.active : ""
                }`}
              >
                <button
                  role="button"
                  tabIndex={0}
                  onClick={() => handleTabChange("original")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleTabChange("original");
                    }
                  }}
                  aria-label={`${getLabel(
                    "video_single_page_original_video",
                    "Originalvideo",
                  )} Button`}
                  className="button-reset"
                >
                  {getLabel(
                    "video_single_page_original_video",
                    "Originalvideo",
                  )}
                </button>
              </li>
              {audioDescriptionVideoDesktop && (
                <li
                  className={`${styles.tab} ${
                    activeVideo === "audio-description" ? styles.active : ""
                  }`}
                >
                  <button
                    className="button-reset"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleTabChange("audio-description")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleTabChange("audio-description");
                      }
                    }}
                    aria-label={`${getLabel(
                      "video_single_page_audio_description_video",
                      "Video mit Audiobeschreibung",
                    )} Button`}
                  >
                    {getLabel(
                      "video_single_page_audio_description_video",
                      "Video mit Audiobeschreibung",
                    )}
                  </button>
                </li>
              )}
            </ul>
          )}

          {post && (
            <>
              {/* Original Video */}
              {activeVideo === "original" && (
                <>
                  {videoData && videoData?.video_type !== "upload" ? (
                    <ContentBlockerWrapper
                      platformName={videoData.video_type || "unknown"}
                      wpApiUrl={process.env.WP_API_URL!}
                      extraClassName={{ wrapper: `${styles["video-blocker"]}` }}
                    >
                      {originalVideo}
                    </ContentBlockerWrapper>
                  ) : (
                    originalVideo
                  )}
                </>
              )}
              {/* Audio Description Video */}
              {audioDescriptionVideoDesktop &&
                videoData &&
                activeVideo === "audio-description" && (
                  <VidstackPlayer
                    autoplay={false}
                    loop={false}
                    muted={false}
                    video={videoData}
                    video_audio_description={audioDescriptionVideoDesktop}
                    video_decorative={false}
                    singlePage={true}
                    sizes="100vw, (min-width:768px) 80vw, (min-width: 1024px) 67vw"
                  />
                )}
              {/* 🎧 AUDIO */}
              {!isVideo && audioData && audioData.audio && (
                <div className={styles.audioWrapper}>
                  <VidstackAudioPlayer
                    audio={audioData}
                    loop={false}
                    singlePage={true}
                  />
                </div>
              )}
            </>
          )}

          {/* Transcript text */}
          <section>
            {transcript?.text && (
              <>
                <h2 className="h1-pages has-text-centered pt-6">
                  {getLabel("transcript", "Transkription")}
                </h2>
                <div
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(transcript.text),
                  }}
                ></div>
              </>
            )}

            {transcript?.textfile && (
              <ExtendedLink
                extraClass="mx-auto"
                btnType="primary"
                extendedLink={{
                  type: "download",
                  download: {
                    link_target: transcript.textfile,
                    link_text: getLabel(
                      "download_transcript",
                      "Transkript herunterladen",
                    ),
                  },
                  link: {
                    url: "",
                    target: "",
                    title: "",
                    aria_label: "",
                  },
                  email: {
                    link_target: "",
                    link_text: "",
                  },
                  pop_up: {
                    link_text: "",
                    link_aria_label: "",
                  },
                  telephone: {
                    link_target: "",
                    link_text: "",
                    link_aria_label: "",
                  },
                }}
              ></ExtendedLink>
            )}
          </section>
        </div>
      </Section>
    </header>
  );
};
